import { createBrowserRouter } from "react-router";

import { AppLayout } from "@/app/layouts/app-layout";
import { AuthLayout } from "@/app/layouts/auth-layout";
import AccountConnectPage from "@/pages/account/connect";
import ChangeEmailPage from "@/pages/auth/change-email";
import LoginPage from "@/pages/auth/login";
import ResetPasswordPage from "@/pages/auth/reset-password/reset-password";
import SendResetLinkPage from "@/pages/auth/reset-password/send-reset-link";
import SignupPage from "@/pages/auth/signup";
import VerifyEmailPage from "@/pages/auth/verify-email";
import MinePage from "@/pages/mine";
import MineWorkspacePage from "@/pages/mine/workspace";
import { PluginRoutePage } from "@/platform/plugins";
import { ROUTE_PATHS, ROUTE_SEGMENTS } from "@/shared/constants/route-paths";

import { GuestRoute, ProtectedRoute } from "./guards";
import {
  ErrorRoutePage,
  EducationRoutePage,
  HomeRoutePage,
  ItemDetailRoutePage,
  MineFlowRoutePage,
  MineFlowDetailRoutePage,
  MinePluginsRoutePage,
  MineTrashRoutePage,
  OnboardingRoutePage,
  PricingRoutePage,
} from "./lazy-routes";
import { RouteErrorBoundary } from "./route-error-boundary";

export const router = createBrowserRouter([
  {
    path: ROUTE_PATHS.HOME,
    element: <HomeRoutePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <AuthLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTE_PATHS.VERIFY_EMAIL,
        element: <VerifyEmailPage />,
        handle: { title: "验证邮箱", description: "验证你的 Pinbox 账户邮箱。" },
      },
      {
        path: ROUTE_PATHS.CHANGE_EMAIL,
        element: <ChangeEmailPage />,
        handle: { title: "确认更换邮箱", description: "确认你的 Pinbox 账户邮箱变更。" },
      },
    ],
  },
  {
    element: <GuestRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: ROUTE_PATHS.LOGIN,
            element: <LoginPage />,
            handle: { title: "登录", description: "登录 Pinbox，继续查看和整理收藏的网页、图片与笔记。" },
          },
          {
            path: ROUTE_PATHS.SIGNUP,
            element: <SignupPage />,
            handle: { title: "注册", description: "注册 Pinbox，开始收藏和整理网页、图片与笔记。" },
          },
          {
            path: ROUTE_PATHS.SEND_RESET_LINK,
            element: <SendResetLinkPage />,
            handle: { title: "找回密码", description: "通过邮箱找回 Pinbox 账户密码。" },
          },
          {
            path: ROUTE_PATHS.RESET_PASSWORD,
            element: <ResetPasswordPage />,
            handle: { title: "重置密码", description: "重置你的 Pinbox 账户密码。" },
          },
        ],
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: ROUTE_PATHS.ACCOUNT_CONNECT,
        element: <AccountConnectPage />,
        handle: { title: "绑定第三方账号" },
      },
      {
        path: ROUTE_PATHS.PRICING,
        element: <PricingRoutePage />,
        handle: { title: "套餐与价格", description: "查看 Pinbox 套餐与功能，选择适合自己的方案。" },
      },
      {
        path: ROUTE_PATHS.EDUCATION,
        element: <EducationRoutePage />,
        handle: { title: "教育优惠", description: "了解并申请 Pinbox 教育优惠，查看申请进度。" },
      },
      {
        path: ROUTE_PATHS.ONBOARDING,
        element: <OnboardingRoutePage />,
        handle: { title: "开始使用" },
      },
      {
        path: ROUTE_PATHS.ITEM_DETAIL,
        element: <ItemDetailRoutePage />,
        handle: { title: "收藏详情" },
      },
      {
        path: ROUTE_PATHS.MINE,
        element: <AppLayout />,
        children: [
          {
            element: <MinePage />,
            children: [
              {
                index: true,
                element: <MineWorkspacePage />,
                handle: { title: "未分类" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_ALL,
                element: <MineWorkspacePage />,
                handle: { title: "全部收藏" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_SEARCH,
                element: <MineWorkspacePage />,
                handle: { title: "搜索收藏" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_FOLDERS,
                element: <MineWorkspacePage />,
                handle: { title: "文件夹" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_TAGS,
                element: <MineWorkspacePage />,
                handle: { title: "标签" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_TRASH,
                element: <MineTrashRoutePage />,
                handle: { title: "回收站" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_WORKFLOW_DETAIL,
                element: <MineFlowDetailRoutePage />,
                handle: { title: "流程详情" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_WORKFLOW,
                element: <MineFlowRoutePage />,
                handle: { title: "流程" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_DUPLICATES,
                element: <MineWorkspacePage />,
                handle: { title: "查找重复收藏" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_PLUGINS,
                element: <MinePluginsRoutePage />,
                handle: { title: "插件" },
              },
              {
                path: ROUTE_SEGMENTS.MINE_PLUGIN_DETAIL,
                element: <PluginRoutePage />,
                handle: { title: "插件详情" },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: ROUTE_PATHS.ERROR,
    element: <ErrorRoutePage />,
    handle: { title: "页面未找到" },
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "*",
    element: <ErrorRoutePage />,
    handle: { title: "页面未找到" },
  },
]);
